import { memo } from "react";
import { Card, IconButton, Stack, Typography } from "@mui/material";
import {
  APP_COLORS,
  APP_FONT_FAMILIES,
  APP_TEXT_SIZES,
  ENUM_INTRESTED_IN_PRIORITY_LIST,
} from "../../../../enums";
import { AppChip, DeleteIconButton } from "../../../../components/form";
import FlexBox from "../../../../components/layouts/Flexbox";
import { PencilSimple } from "@phosphor-icons/react";

const PRIORITY_STYLES: Record<
  string,
  { cardBg: string; chipBg: string; chipColor: string }
> = {
  [ENUM_INTRESTED_IN_PRIORITY_LIST.HIGH]: {
    cardBg: APP_COLORS.successLight2,
    chipBg: APP_COLORS.successLight3,
    chipColor: APP_COLORS.successDark3,
  },
  [ENUM_INTRESTED_IN_PRIORITY_LIST.MEDIUM]: {
    cardBg: APP_COLORS.secondaryLight,
    chipBg: APP_COLORS.secondaryLight3,
    chipColor: APP_COLORS.secondaryDark3,
  },
  [ENUM_INTRESTED_IN_PRIORITY_LIST.LOW]: {
    cardBg: APP_COLORS.errorLight,
    chipBg: APP_COLORS.errorLight3,
    chipColor: APP_COLORS.errorDark3,
  },
};

const DEFAULT_STYLES = {
  cardBg: APP_COLORS.successLight2,
  chipBg: APP_COLORS.successLight3,
  chipColor: APP_COLORS.secondaryDark3,
};

const ICON_BUTTON_SX = { p: 0, verticalAlign: "middle" } as const;

interface IntrestedBlockData {
  label?: string;
  priority?: string;
}

interface IntrestedBlockProps {
  data: IntrestedBlockData | null | undefined;
  editable?: boolean;
  onEditClick: () => void;
  onDeleteClick: () => void;
  deleteLoading?: boolean;
}

function IntrestedBlock({
  data,
  editable,
  onEditClick,
  onDeleteClick,
  deleteLoading = false,
}: IntrestedBlockProps) {
  const priority = data?.priority?.toLowerCase() ?? "";
  const styles =
    PRIORITY_STYLES[priority] ?? DEFAULT_STYLES;

  return (
    <Card sx={{ backgroundColor: styles.cardBg, border: 0 }}>
      <Stack gap={1}>
        <FlexBox
          alignItems="center"
          p={1}
          bgcolor={APP_COLORS.white}
          borderRadius={3}
        >
          <Typography
            variant="body-mono-medium"
            className="truncated-text"
            title={data?.label}
          >
            {data?.label}
          </Typography>
        </FlexBox>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <AppChip
            label={data?.priority?.toUpperCase()}
            icon={<></>}
            size="small"
            sx={{
              bgcolor: styles.chipBg,
              color: styles.chipColor,
              fontSize: APP_TEXT_SIZES.xxsmall,
              fontFamily: APP_FONT_FAMILIES.ibmPlexMono,
              fontWeight: 500,
            }}
          />
          {editable && (
            <Stack direction="row" alignItems="center" gap={.5}>
              <IconButton sx={ICON_BUTTON_SX} onClick={onEditClick} size="small">
                <PencilSimple size={20} />
              </IconButton>
              <DeleteIconButton onClick={onDeleteClick} loading={deleteLoading} />
            </Stack>
          )}
        </Stack>
      </Stack>
    </Card>
  );
}

export default memo(IntrestedBlock);
